/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Global Cursor Component
const GlobalCursor = () => {
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const trailRef = useRef(null);
  const dotArray = useRef([]);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const cursor = cursorRef.current;
    const cursorDot = cursorDotRef.current;
    const trail = trailRef.current;
    const dots = dotArray.current;

    if (!cursor || !cursorDot || !trail) return;

    // Hide default cursor on desktop
    if (!isMobile) {
      document.body.style.cursor = 'none';
    }

    const handleMouseMove = (e) => {
      if (isMobile) return;
      
      // Main cursor with delay
      gsap.to(cursor, {
        x: e.clientX - 25,
        y: e.clientY - 25,
        duration: 0.4,
        ease: "power2.out"
      });

      // Cursor dot with more delay
      gsap.to(cursorDot, {
        x: e.clientX - 4,
        y: e.clientY - 4,
        duration: 0.8,
        ease: "power2.out"
      });

      // Trail effect
      gsap.to(trail, {
        x: e.clientX - 80,
        y: e.clientY - 80,
        duration: 1.2,
        ease: "power2.out"
      });

      // Animate dot array with different delays
      dots.forEach((dot, index) => {
        if (dot) {
          gsap.to(dot, {
            x: e.clientX - 6,
            y: e.clientY - 6,
            duration: 0.6 + (index * 0.2),
            ease: "power2.out"
          });
        }
      });
    };

    const handleTouchMove = (e) => {
      if (!isMobile) return;
      
      const touch = e.touches[0];

      // Animate cursor elements for touch
      gsap.to(cursor, {
        x: touch.clientX - 25,
        y: touch.clientY - 25,
        duration: 0.3,
        ease: "power2.out"
      });

      gsap.to(cursorDot, {
        x: touch.clientX - 4,
        y: touch.clientY - 4,
        duration: 0.5,
        ease: "power2.out"
      });

      gsap.to(trail, {
        x: touch.clientX - 80,
        y: touch.clientY - 80,
        duration: 0.8,
        ease: "power2.out"
      });

      // Animate dot array for touch
      dots.forEach((dot, index) => {
        if (dot) {
          gsap.to(dot, {
            x: touch.clientX - 6,
            y: touch.clientY - 6,
            duration: 0.4 + (index * 0.15),
            ease: "power2.out"
          });
        }
      });
    };

    const handleMouseEnter = (e) => {
      const target = e.target;

      if (target instanceof Element) {
        const isInteractive = target.matches(
          'a, button, [role="button"], input, textarea, select, .interactive'
        );

        if (isInteractive) {
          setIsHovering(true);
          
          // Make cursor transparent on buttons/interactive elements
          gsap.to(cursor, {
            scale: 2.2,
            opacity: 0.3,
            duration: 0.3,
            ease: "power2.out"
          });
          
          gsap.to(trail, {
            scale: 1.5,
            opacity: 0.2,
            duration: 0.3,
            ease: "power2.out"
          });

          // Scale dot array
          dots.forEach((dot, index) => {
            if (dot) {
              gsap.to(dot, {
                scale: 1.5 + (index * 0.2),
                duration: 0.3,
                ease: "power2.out"
              });
            }
          });
        }
      }
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
      
      gsap.to(cursor, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      });
      
      gsap.to(trail, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      });

      // Reset dot array
      dots.forEach((dot) => {
        if (dot) {
          gsap.to(dot, {
            scale: 1,
            duration: 0.3,
            ease: "power2.out"
          });
        }
      });
    };

    const handleTouchStart = (e) => {
      if (!isMobile) return;

      // Show cursor on touch
      gsap.to([cursor, cursorDot, trail], {
        opacity: 1,
        duration: 0.2
      });
    };

    const handleTouchEnd = () => {
      if (!isMobile) return;
      
      // Hide cursor after touch
      gsap.to([cursor, cursorDot, trail], {
        opacity: 0,
        duration: 0.5,
        delay: 0.5
      });
    };

    // Add event listeners
    if (!isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseenter', handleMouseEnter, true);
      document.addEventListener('mouseleave', handleMouseLeave, true);
    } else {
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.body.style.cursor = 'auto';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', checkMobile);
    };
  }, [isMobile]);

  return (
    <>
      {/* Trail Effect */}
      <div
        ref={trailRef}
        className="fixed w-40 h-40 pointer-events-none z-50"
        style={{
          background: 'radial-gradient(circle, rgba(52, 211, 153, 0.25) 0%, rgba(52, 211, 153, 0.15) 40%, rgba(30, 58, 138, 0.1) 70%, transparent 100%)',
          borderRadius: '50%',
          filter: 'blur(30px)',
          mixBlendMode: 'screen',
          opacity: isMobile ? 0 : 1
        }}
      />
      
      {/* Main Cursor */}
      <div
        ref={cursorRef}
        className="fixed w-12 h-12 pointer-events-none z-50"
        style={{
          background: isHovering 
            ? 'radial-gradient(circle, rgba(52, 211, 153, 0.3) 0%, rgba(30, 58, 138, 0.2) 50%, rgba(52, 211, 153, 0.1) 100%)'
            : 'radial-gradient(circle, rgba(52, 211, 153, 0.8) 0%, rgba(52, 211, 153, 0.4) 60%, transparent 100%)',
          borderRadius: '50%',
          border: '2px solid rgba(52, 211, 153, 0.9)',
          backdropFilter: 'blur(2px)',
          boxShadow: '0 0 20px rgba(52, 211, 153, 0.5), inset 0 0 20px rgba(52, 211, 153, 0.2)',
          opacity: isMobile ? 0 : 1
        }}
      />
      
      {/* Cursor Dot */}
      <div
        ref={cursorDotRef}
        className="fixed w-3 h-3 rounded-full pointer-events-none z-50"
        style={{
          backgroundColor: '#34D399',
          boxShadow: '0 0 10px rgba(52, 211, 153, 0.8)',
          opacity: isMobile ? 0 : 1
        }}
      />

      {/* Four Dot Array with different sizes */}
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          ref={el => dotArray.current[index] = el}
          className="fixed rounded-full pointer-events-none z-50"
          style={{
            width: `${8 + index * 2}px`,
            height: `${8 + index * 2}px`,
            backgroundColor: `rgba(52, 211, 153, ${0.8 - index * 0.15})`,
            boxShadow: `0 0 ${5 + index * 3}px rgba(52, 211, 153, ${0.6 - index * 0.1})`,
            opacity: isMobile ? 0 : 1
          }}
        />
      ))}

      
    </>
  );
};

export default GlobalCursor;